import React, { useCallback, useState, useRef } from 'react';
import './DashboardContaUsuario.scss';
import api from '../../../../config/configApi';
import { useAuth } from '../../../../context/AuthContext';
import avatar from '../../../../assets/images/user.png';
import { Button, Box, Breadcrumbs, Chip, emphasize, FormControl, FormLabel, InputAdornment, styled, TextField, Alert } from '@mui/material';
import { MdHome, MdVisibility } from 'react-icons/md';
import { isEmpty } from 'lodash';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import Resizer from "react-image-file-resizer";
import { LazyLoadImage } from 'react-lazy-load-image-component';

const StyledBreadcrumb = styled(Chip)(({ theme }) => {
    return {
        backgroundColor: theme.palette.grey[100],
        height: theme.spacing(3),
        color: (theme.vars || theme).palette.text.primary,
        fontWeight: theme.typography.fontWeightRegular,
        '&:hover, &:focus': {
            backgroundColor: emphasize(theme.palette.grey[100], 0.06),
            ...theme.applyStyles('dark', {
                backgroundColor: emphasize(theme.palette.grey[800], 0.06),
            }),
        },
        '&:active': {
            boxShadow: theme.shadows[1],
            backgroundColor: emphasize(theme.palette.grey[100], 0.12),
            ...theme.applyStyles('dark', {
                backgroundColor: emphasize(theme.palette.grey[800], 0.12),
            }),
        },
        ...theme.applyStyles('dark', {
            backgroundColor: theme.palette.grey[800],
        }),
    };
});

function DashboardContaUsuario() {
    const { user, updateUser, logout } = useAuth();
    const navigate = useNavigate();

    const [isButtonDisabled, setIsButtonDisabled] = useState(false);
    const [nomeFocused, setNomeFocused] = useState(false);
    const [senhaAtualFocused, setSenhaAtualFocused] = useState(false);
    const [senhaNovaFocused, setSenhaNovaFocused] = useState(false);
    const [senhaConfirmFocused, setSenhaConfirmFocused] = useState(false);
    const [showSenhaAtual, setShowSenhaAtual] = useState(false);
    const [showSenhaNova, setShowSenhaNova] = useState(false);
    const [showSenhaConfirm, setShowSenhaConfirm] = useState(false);
    const [userForm, setUserForm] = useState({
        nome: user?.nome || '',
    })
    const [formError, setFormError] = useState({
        nome: null,
        old_password: null,
        password: null,
        confirm_password: null
    });

    // ESTADOS E HANDLERS DE IMAGEM

    const [selectedFile, setSelectedFile] = useState();
    const [previewURL, setPreviewURL] = useState();
    const fileInputRef = useRef(null);

    function selectFiles() {
        fileInputRef.current.click();
    }

    const handleImageChange = async (event) => {
        const file = event.target.files[0];        

        Resizer.imageFileResizer(
            file,
            800,
            800,
            'JPEG',
            100,
            0,
            (uri) => {
                setSelectedFile(uri)
                setPreviewURL(URL.createObjectURL(uri));
            },
            'blob'
        );

    };

    const handleChange = (e) => {
        setUserForm((prev) => ({
            ...prev,
            [e.target.name]: e.target.value
        }));
    }

    const handleClickShowSenhaAtual = () => {
        setShowSenhaAtual(!showSenhaAtual);
    }

    const handleClickShowSenhaNova = () => {
        setShowSenhaNova(!showSenhaNova);
    }

    const handleClickShowSenhaConfirm = () => {
        setShowSenhaConfirm(!showSenhaConfirm);
    }

    const validateForm = useCallback(() => {
        let currentErrors = {};
        let formIsValid = true;

        if (isEmpty(userForm.nome)) {
            currentErrors.nome = 'Preencha o nome';
            formIsValid = false;
        }
        if (userForm.password || userForm.old_password || userForm.confirm_password) {
            if (isEmpty(userForm.old_password)) {
                currentErrors.old_password = 'Preencha a senha atual';
                formIsValid = false;
            }
            if (isEmpty(userForm.password)) {
                currentErrors.password = 'Preencha a nova senha';
                formIsValid = false;
            }
            if (isEmpty(userForm.confirm_password)) {
                currentErrors.confirm_password = 'Preencha a confirmação da senha';
                formIsValid = false;
            }
            if (userForm.password === userForm.old_password) {
                currentErrors.password = 'Nova senha não pode ser igual a senha atual';
                formIsValid = false;
            }
            if (userForm.confirm_password !== userForm.password) {
                currentErrors.confirm_password = 'Confirmação da senha não pode ser diferente da senha nova';
                formIsValid = false;
            }
        }

        setFormError(currentErrors);
        return formIsValid;
    }, [userForm])

    const handleSubmit = async (e) => {
        e.preventDefault();

        const formIsValid = validateForm();
        if (!formIsValid) {
            toast.error('Por favor, corrija os erros no formulário')
            return;
        }

        setIsButtonDisabled(true);
        const toastId = toast.loading('Atualizando perfil...');

        const formData = new FormData();
        let hasChanges = false; // Flag para verificar se ha algo para enviar
        const updatedContextData = {};

        // Adicionar nome se alterado
        if (userForm.nome !== user.nome) {
            formData.append('nome', userForm.nome);
            updatedContextData.nome = userForm.nome;
            hasChanges = true;
        }
        // Adicionar dados de senha se houver tentativa de alteração
        if (userForm.old_password && userForm.password) {
            formData.append('old_password', userForm.old_password);
            formData.append('password', userForm.password);
            hasChanges = true;
        }
        // Adicionar arquivo de avatar se selecionado
        if (selectedFile) {
            formData.append('avatar', selectedFile);
            updatedContextData.avatar = previewURL;
            hasChanges = true;
        }

        // 3. Verificar se há alguma alteração para enviar
        if (!hasChanges) {
            toast.error('Nenhuma informação foi alterada para salvar!', { id: toastId });
            setIsButtonDisabled(false);
            return;
        }

        try {
            const response = await api.patch(`/api/auth/update-user/${user.id}`, formData);            

            if (response.status === 200) {
                setUserForm(prev => ({
                    ...prev,
                    old_password: '',
                    password: '',
                    confirm_password: '',
                }));
                toast.success('Perfil atualizado com sucesso!', { id: toastId, duration: 5000 });
                if (Object.keys(updatedContextData).length > 0) {
                    updateUser(updatedContextData);
                }
                toast('Logue novamente na conta', { icon: '⚠️', duration: 5000 });
                try {
                    const res = await logout();
                    if (res && res.status === 200) {
                        toast.success(`${res.data.msg}`);
                        setTimeout(() => navigate('/login'), 2000);
                    } else {
                        toast.error(`Falha no logout: ${res.data.message || 'Erro ao fazer logout.'}`);
                    }
                } catch (err) {
                    const errorMessage = err.response?.data?.msg || err.message || 'Erro inesperado ao fazer logout.';
                    toast.error(`Erro inesperado ao fazer logout: ${errorMessage}`);
                }
            } else {
                toast.error(`Falha ao atualizar perfil: ${response.data.message || 'Erro desconhecido ao atualizar perfil'}`, { id: toastId });
            }

        } catch (err) {
            const errorMessage = err.response?.data?.msg || err.message || 'Erro desconhecido ao atualizar perfil.';
            if (err.status !== 500) {
                toast.error(`Erro: ${errorMessage}`, { id: toastId });
            }
            if (err.status === 500) {
                toast.error(`Erro inesperado ao atualizar perfil: ${errorMessage}`, { id: toastId });
            }
        } finally {
            setIsButtonDisabled(false);
        }
    }

    return (
        <>
            <div className='card shadow border-0 w-100 flex-row p-4 res-col'>
                <h5 className='mb-0'>Conta</h5>
                <Breadcrumbs
                    aria-label='breadcrumb'
                    className='ms-auto breadcrumbs_'
                >
                    <StyledBreadcrumb label='Dashboard' icon={<MdHome fontSize='small' />} />
                    <StyledBreadcrumb label='Usuário' />
                    <StyledBreadcrumb label='Conta' />
                </Breadcrumbs>
            </div>
            {!user.atualizar && (
                <Alert severity='warning'>Seu usuário não possui permissão de editar!</Alert>
            )}
            {user.atualizar && (
                <Box
                    className='form'
                    component='form'
                    onSubmit={handleSubmit}
                    noValidate
                >
                    <div className='row'>
                        <div className='col-md-12'>
                            <div className='card p-4 mt-0'>
                                <h5 className='mb-4'>Informações</h5>

                                <div className='row'>
                                    <div className='col'>
                                        <FormControl
                                            className='mb-4'
                                            fullWidth
                                        >
                                            <FormLabel
                                                htmlFor='file'
                                                sx={{ fontSize: '12px', fontWeight: 500, marginBottom: '5px' }}
                                            >
                                                Foto do perfil
                                            </FormLabel>

                                            <div className='d-flex align-items-center'>
                                                <div className='userImg me-2'>
                                                    <span className='rounded-circle'>
                                                        {previewURL ? (
                                                            <LazyLoadImage src={previewURL} alt='avatar' />
                                                        ) : (
                                                            <LazyLoadImage src={user.avatar !== null ? user.avatar : avatar} alt='avatar' />
                                                        )}
                                                    </span>
                                                </div>
                                                <div className='upload'>
                                                    <input
                                                        name='avatar'
                                                        type='file'
                                                        accept='image/*'
                                                        onChange={handleImageChange}
                                                        disabled={isButtonDisabled}
                                                        ref={fileInputRef}
                                                    />
                                                    <Button
                                                        variant='outlined'
                                                        onClick={selectFiles}
                                                    >
                                                        Alterar foto perfil
                                                    </Button>
                                                </div>
                                            </div>
                                        </FormControl>
                                    </div>

                                    <div className='col'>
                                        <FormControl
                                            className='mb-4'
                                            fullWidth
                                        >
                                            <FormLabel
                                                htmlFor='nome'
                                                sx={{ fontSize: '12px', fontWeight: 500, marginBottom: '5px', color: nomeFocused ? 'primary.main' : '' }}
                                                error={formError?.nome}
                                            >
                                                Nome
                                            </FormLabel>
                                            <TextField
                                                required
                                                id='nome'
                                                name='nome'
                                                type='text'
                                                variant='outlined'
                                                value={userForm.nome}
                                                error={formError?.nome}
                                                helperText={formError?.nome}
                                                color={formError?.nome ? 'error' : 'primary'}
                                                onChange={handleChange}
                                                onFocus={() => setNomeFocused(!nomeFocused)}
                                                onBlur={() => setNomeFocused(!nomeFocused)}
                                            />
                                        </FormControl>
                                    </div>

                                    <div className='col'>
                                        <FormControl
                                            className='mb-4'
                                            fullWidth
                                        >
                                            <FormLabel
                                                htmlFor='old_password'
                                                sx={{ fontSize: '12px', fontWeight: 500, marginBottom: '5px', color: senhaAtualFocused ? 'primary.main' : '' }}
                                                error={formError?.old_password}
                                            >
                                                Senha atual
                                            </FormLabel>
                                            <TextField
                                                required
                                                id='old_password'
                                                name='old_password'
                                                type={showSenhaAtual ? 'text' : 'password'}
                                                variant='outlined'
                                                error={formError?.old_password}
                                                helperText={formError?.old_password}
                                                color={formError?.old_password ? 'error' : 'primary'}
                                                onChange={handleChange}
                                                onFocus={() => setSenhaAtualFocused(!senhaAtualFocused)}
                                                onBlur={() => setSenhaAtualFocused(!senhaAtualFocused)}
                                                slotProps={{
                                                    input: {
                                                        endAdornment: (
                                                            <InputAdornment position='end'>
                                                                <MdVisibility onClick={handleClickShowSenhaAtual} style={{ cursor: 'pointer' }} />
                                                            </InputAdornment>
                                                        ),
                                                    },
                                                }}
                                            />
                                        </FormControl>
                                    </div>
                                </div>

                                <div className='row'>
                                    <div className='col'>
                                        <FormControl
                                            className='mb-4'
                                            fullWidth
                                        >
                                            <FormLabel
                                                htmlFor='password'
                                                sx={{ fontSize: '12px', fontWeight: 500, marginBottom: '5px', color: senhaNovaFocused ? 'primary.main' : '' }}
                                                error={formError?.password}
                                            >
                                                Senha nova
                                            </FormLabel>
                                            <TextField
                                                required
                                                id='password'
                                                name='password'
                                                type={showSenhaNova ? 'text' : 'password'}
                                                variant='outlined'
                                                error={formError?.password}
                                                helperText={formError?.password}
                                                color={formError?.password ? 'error' : 'primary'}
                                                onChange={handleChange}
                                                onFocus={() => setSenhaNovaFocused(!senhaNovaFocused)}
                                                onBlur={() => setSenhaNovaFocused(!senhaNovaFocused)}
                                                slotProps={{
                                                    input: {
                                                        endAdornment: (
                                                            <InputAdornment position='end'>
                                                                <MdVisibility onClick={handleClickShowSenhaNova} style={{ cursor: 'pointer' }} />
                                                            </InputAdornment>
                                                        ),
                                                    },
                                                }}
                                            />
                                        </FormControl>
                                    </div>

                                    <div className='col'>
                                        <FormControl
                                            className='mb-4'
                                            fullWidth
                                        >
                                            <FormLabel
                                                htmlFor='confirm_password'
                                                sx={{ fontSize: '12px', fontWeight: 500, marginBottom: '5px', color: senhaConfirmFocused ? 'primary.main' : '' }}
                                                error={formError?.confirm_password}
                                            >
                                                Confirmar senha
                                            </FormLabel>
                                            <TextField
                                                required
                                                id='confirm_password'
                                                name='confirm_password'
                                                type={showSenhaConfirm ? 'text' : 'password'}
                                                variant='outlined'
                                                error={formError?.confirm_password}
                                                helperText={formError?.confirm_password}
                                                color={formError?.confirm_password ? 'error' : 'primary'}
                                                onChange={handleChange}
                                                onFocus={() => setSenhaConfirmFocused(!senhaConfirmFocused)}
                                                onBlur={() => setSenhaConfirmFocused(!senhaConfirmFocused)}
                                                slotProps={{
                                                    input: {
                                                        endAdornment: (
                                                            <InputAdornment position='end'>
                                                                <MdVisibility onClick={handleClickShowSenhaConfirm} style={{ cursor: 'pointer' }} />
                                                            </InputAdornment>
                                                        ),
                                                    },
                                                }}
                                            />
                                        </FormControl>
                                    </div>
                                </div>

                                <div className='d-flex justify-content-between'>
                                    <Button
                                        color='error'
                                        variant='contained'
                                        onClick={() => navigate('/dashboard')}
                                        disabled={isButtonDisabled}
                                    >
                                        Cancelar
                                    </Button>

                                    <Button
                                        type='submit'
                                        variant='contained'
                                        sx={{ color: '#fff' }}
                                        loading={isButtonDisabled}
                                    >
                                        Salvar
                                    </Button>
                                </div>
                            </div>
                        </div>
                    </div>
                </Box>
            )}
        </>
    )
}

export default DashboardContaUsuario;